import type { ReactNode } from "react";

import type { VideoPlayerProps } from "@ui/components/VideoPlayer";

import { Container } from "@jti/core-components/components/Container";

import styles from "./VideoContainer.module.scss";

export type VideoPlayerContainerProps = {
  videoType?: "youtube" | "hosted internally";
  youtubeConfiguration?: {
    externalLinkVideo: string;
  };
  hostedVideoConfiguration?: {
    loop?: boolean;
    autoPlay?: boolean;
    desktopSrc?: string;
    tabletSrc?: string;
    mobileSrc?: string;
  };
  controlColor?: "dark" | "light";
  posterImage?: string;
  posterAlt?: string;
  description?: string;
  duration?: boolean;
  showFullscreen?: boolean;
  showControls?: boolean;
  preventMainPlayButtonClick?: boolean;
  preload?: boolean;
  contentSize?: boolean;
  title?: string;
  playLabel?: string;
  replayLabel?: string;
  muteLabel?: string;
  soundOnLabel?: string;
  seekLabel?: string;
  volumeLabel?: string;
  showFullScreenLabel?: string;
};

export function videoPlayerContainerPropsToVideoPlayerProps(
  containerProps: VideoPlayerContainerProps
): VideoPlayerProps {
  const isYoutube = containerProps.videoType === "youtube";

  return {
    poster: containerProps.posterImage,
    description: containerProps.description,
    showDuration: containerProps.duration,
    showFullscreen: containerProps.showFullscreen,
    showControls: containerProps.showControls,
    preventMainPlayButtonClick: containerProps.preventMainPlayButtonClick,
    preload: containerProps.preload,
    contentSize: containerProps.contentSize,
    themeClass: containerProps.controlColor === "light" ? "dark-theme" : "light-theme",
    hostedExternally: isYoutube,
    externalLinkVideo: isYoutube ? containerProps.youtubeConfiguration?.externalLinkVideo : undefined,
    desktopSrc: isYoutube ? "" : (containerProps.hostedVideoConfiguration?.desktopSrc ?? ""),
    tabletSrc: containerProps.hostedVideoConfiguration?.tabletSrc,
    mobileSrc: containerProps.hostedVideoConfiguration?.mobileSrc,
    loop: containerProps.hostedVideoConfiguration?.loop,
    autoPlay: containerProps.hostedVideoConfiguration?.autoPlay,
    alt: containerProps.posterAlt,
    title: containerProps.title,
    playLabel: containerProps.playLabel,
    replayLabel: containerProps.replayLabel,
    muteLabel: containerProps.muteLabel,
    soundOnLabel: containerProps.soundOnLabel,
    seekLabel: containerProps.seekLabel,
    volumeLabel: containerProps.volumeLabel,
    showFullscreenLabel: containerProps.showFullScreenLabel,
  };
}

type VideoContainerProps = {
  title: string;
  items: ReactNode[];
};

export function VideoContainer({ title, items }: VideoContainerProps) {
  return (
    <Container className={styles.container}>
      <div className={styles.title}>{title}</div>
      <div className={styles.wrapper}>
        {items.map((item, index) => (
          <div key={index} className={styles.item}>
            {item}
          </div>
        ))}
      </div>
    </Container>
  );
}
